import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  createProblemSpec,
  fetchProblemSpec,
  updateProblemSpec,
} from '../services/api';

const emptyForm = {
  nameTh: '',
  nameEn: '',
  description: '',
  maxSize: 0,
  maxCount: 0,
  severity: 'medium',
};

export default function SpecFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return undefined;
    let alive = true;
    setLoading(true);
    fetchProblemSpec(id)
      .then((data) => {
        if (!alive || !data) return;
        setForm({
          nameTh: data.nameTh || '',
          nameEn: data.nameEn || '',
          description: data.description || '',
          maxSize: data.maxSize ?? 0,
          maxCount: data.maxCount ?? 0,
          severity: data.severity || 'medium',
        });
        setExistingImages(Array.isArray(data.images) ? data.images : []);
      })
      .catch((err) => {
        if (alive) setError(err.message || 'โหลดไม่สำเร็จ');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, isEdit]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nameTh.trim()) {
      setError('กรุณาระบุชื่อปัญหา (ภาษาไทย)');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        unit: 'mm',
        maxSize: Number(form.maxSize) || 0,
        maxCount: Number(form.maxCount) || 0,
        images: existingImages,
      };
      if (isEdit) {
        await updateProblemSpec(id, payload, []);
      } else {
        await createProblemSpec(payload, []);
      }
      navigate('/spec');
    } catch (err) {
      setError(err.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="card muted">กำลังโหลด...</div>;
  }

  return (
    <div className="stack spec-form-page">
      <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <Link className="btn" to="/spec" aria-label="กลับ">
          ←
        </Link>
        <h2 style={{ margin: 0 }}>{isEdit ? 'แก้ไขปัญหา' : 'เพิ่มปัญหาใหม่'}</h2>
      </div>

      <form className="card stack" onSubmit={handleSubmit}>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="nameTh">
              ชื่อปัญหา (ภาษาไทย) <span style={{ color: 'var(--ng)' }}>*</span>
            </label>
            <input
              id="nameTh"
              value={form.nameTh}
              onChange={(e) => setField('nameTh', e.target.value)}
              placeholder="เช่น จุดดำ"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="nameEn">ชื่อปัญหา (English)</label>
            <input
              id="nameEn"
              value={form.nameEn}
              onChange={(e) => setField('nameEn', e.target.value)}
              placeholder="e.g. Black Dot"
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="description">
            รายละเอียดเงื่อนไขปัญหา (ระบุให้ละเอียด ไม่จำกัดความยาว)
          </label>
          <textarea
            id="description"
            className="spec-textarea"
            rows={5}
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="ระบุเงื่อนไข เช่น ลักษณะปัญหา สาเหตุ วิธีสังเกต และขนาดที่ยอมรับได้..."
          />
        </div>

        <div className="spec-form-metrics spec-form-metrics-3">
          <div className="field">
            <label htmlFor="maxSize">ขนาดสูงสุด (mm)</label>
            <input
              id="maxSize"
              type="number"
              min="0"
              step="0.1"
              value={form.maxSize}
              onChange={(e) => setField('maxSize', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="maxCount">จำนวนสูงสุด</label>
            <input
              id="maxCount"
              type="number"
              min="0"
              step="1"
              value={form.maxCount}
              onChange={(e) => setField('maxCount', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="severity">ระดับความรุนแรง</label>
            <select
              id="severity"
              value={form.severity}
              onChange={(e) => setField('severity', e.target.value)}
            >
              <option value="high">สูง (High)</option>
              <option value="medium">กลาง (Medium)</option>
              <option value="low">ต่ำ (Low)</option>
            </select>
          </div>
        </div>

        {error && <p style={{ color: 'var(--ng)', margin: 0 }}>{error}</p>}

        <div className="spec-form-actions">
          <Link className="btn" to="/spec">
            ยกเลิก
          </Link>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึกปัญหา'}
          </button>
        </div>
      </form>
    </div>
  );
}
